import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  Input,
  Select,
  Switch,
  Upload,
  Space,
  Modal,
  List,
  Image,
  Popconfirm,
  Card,
  Tag,
  Row,
  Col,
  Typography,
  Empty,
  InputNumber,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  LinkOutlined,
  ShopOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCustomModuleItems,
  createCustomModuleItem,
  updateCustomModuleItemAction,
  deleteCustomModuleItem,
} from "../slice";
import { getPhotoThumbnailUrl, getBase64, getPhotoUrl } from "utils/common";
import { toast } from "react-toastify";

const { Text, Title } = Typography;

const CustomModuleItemsList = () => {
  const dispatch = useDispatch();
  const {
    customModuleItems: items,
    isItemsLoading: isLoading,
    isItemsProcessing: isProcessing,
  } = useSelector((state) => state.config);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [image, setImage] = useState(null);

  useEffect(() => {
    dispatch(fetchCustomModuleItems());
  }, [dispatch]);

  const handleShowModal = (item = null) => {
    setEditingItem(item);
    setIsModalVisible(true);
    setImage(null);

    if (item) {
      console.log("Editing item:", item); // Debug log

      form.setFieldsValue({
        type: item.type || "null",
        url: item.url || "",
        openBrowser: item.openBrowser || false,
        restaurant: item.restaurant || "",
        priority: item.priority || 0,
      });
      if (item.image?.path) {
        setImageUrl(getPhotoThumbnailUrl(item.image.path));
      } else {
        setImageUrl(null);
      }
    } else {
      form.resetFields();
      setImageUrl(null);
    }
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setEditingItem(null);
    form.resetFields();
    setImage(null);
    setImageUrl(null);
  };

  const beforeUpload = async (file) => {
    const validType =
      file.type === "image/jpeg" ||
      file.type === "image/png" ||
      file.type === "image/webp";
    if (!validType) {
      toast.error("Chỉ chấp nhận file ảnh!");
    } else {
      const preview = await getBase64(file);
      setImageUrl(preview);
      setImage(file);
    }
    return false;
  };

  const handleSubmit = async (values) => {
    // Validate image for new items
    if (!editingItem && !image) {
      toast.error("Vui lòng chọn ảnh!");
      return;
    }

    const itemData = {
      ...values,
    };

    // Add image if there's a new file, otherwise keep existing image
    if (image) {
      itemData.image = image;
    } else if (editingItem?.image) {
      // Keep existing image when editing and no new file selected
      itemData.image = editingItem.image.path || editingItem.image;
    }

    console.log("Submitting item data:", itemData);
    console.log("Image object:", image);

    try {
      if (editingItem) {
        // Update existing item
        await dispatch(updateCustomModuleItemAction(editingItem._id, itemData));
      } else {
        // Create new item
        await dispatch(createCustomModuleItem(itemData));
      }
      handleCloseModal();
    } catch (error) {
      console.error("Error submitting item:", error);
    }
  };

  const handleDelete = async (item) => {
    try {
      await dispatch(deleteCustomModuleItem(item._id));
    } catch (error) {
      console.error("Error deleting item:", error);
    }
  };

  const getUploadButton = () => (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  const getTypeInfo = (type) => {
    switch (type) {
      case "url":
        return { label: "Liên kết", color: "blue", icon: <LinkOutlined /> };
      case "restaurant":
        return { label: "Nhà hàng", color: "green", icon: <ShopOutlined /> };
      default:
        return { label: "Không dùng", color: "default", icon: null };
    }
  };

  const renderListItem = (item, index) => {
    const typeInfo = getTypeInfo(item.type);

    return (
      <Card
        hoverable
        style={{
          width: "100%",
          marginBottom: 16,
        }}
        bodyStyle={{ padding: 16 }}
      >
        <Row gutter={16} align="middle">
          {/* Priority Badge */}
          <Col xs={2} sm={1}>
            <div
              style={{
                backgroundColor: "#1890ff",
                color: "white",
                borderRadius: "50%",
                width: 24,
                height: 24,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                fontWeight: "bold",
              }}
            >
              {item.priority || 0}
            </div>
          </Col>

          {/* Image */}
          <Col xs={22} sm={8}>
            <div style={{ textAlign: "center", marginBottom: 8 }}>
              <Image
                width={210}
                height={80}
                src={getPhotoUrl(item.image?.path)}
                style={{
                  objectFit: "cover",
                  borderRadius: 8,
                  border: "1px solid #f0f0f0",
                }}
              />
            </div>
          </Col>

          {/* Content */}
          <Col xs={24} sm={8}>
            <div style={{ marginBottom: 8 }}>
              <Tag
                color={typeInfo.color}
                icon={typeInfo.icon}
                style={{ marginBottom: 8 }}
              >
                {typeInfo.label}
              </Tag>
            </div>

            {item.type === "url" && (
              <div style={{ marginBottom: 8 }}>
                <Text strong>URL: </Text>
                <Text copyable ellipsis style={{ maxWidth: 300 }}>
                  {item.url || "-"}
                </Text>
                <br />
                <Text strong>Mở trình duyệt: </Text>
                <Tag color={item.openBrowser ? "success" : "default"}>
                  {item.openBrowser ? "Có" : "Không"}
                </Tag>
              </div>
            )}

            {item.type === "restaurant" && (
              <div style={{ marginBottom: 8 }}>
                <Text strong>ID Nhà hàng: </Text>
                <Text code>{item.restaurant || "-"}</Text>
              </div>
            )}

            {item.type === "null" && (
              <Text type="secondary">Không có cấu hình đặc biệt</Text>
            )}
          </Col>

          {/* Actions */}
          <Col xs={24} sm={6} style={{ textAlign: "right" }}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={() => handleShowModal(item)}
                block
                size="small"
              >
                Chỉnh sửa
              </Button>
              <Popconfirm
                title="Bạn có chắc chắn muốn xóa item này?"
                onConfirm={() => handleDelete(item)}
                okText="Có"
                cancelText="Không"
                placement="topRight"
              >
                <Button danger icon={<DeleteOutlined />} block size="small">
                  Xóa
                </Button>
              </Popconfirm>
            </Space>
          </Col>
        </Row>
      </Card>
    );
  };

  return (
    <div>
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => handleShowModal()}
          style={{
            borderRadius: 6,
          }}
        >
          Thêm item
        </Button>

        <Text type="secondary" style={{ fontSize: 12 }}>
          Sắp xếp theo độ ưu tiên (thấp → cao)
        </Text>
      </div>

      {items.length === 0 && !isLoading ? (
        <Empty
          description="Chưa có item nào"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{
            margin: "40px 0",
            padding: "40px",
            backgroundColor: "#fafafa",
            borderRadius: 8,
          }}
        >
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => handleShowModal()}
          >
            Tạo item đầu tiên
          </Button>
        </Empty>
      ) : (
        <div
          style={{ backgroundColor: "#fafafa", padding: 16, borderRadius: 8 }}
        >
          <List
            loading={isLoading}
            dataSource={items}
            renderItem={(item, index) => (
              <List.Item key={item._id || index} style={{ padding: 0 }}>
                {renderListItem(item, index)}
              </List.Item>
            )}
          />
        </div>
      )}

      <Modal
        title={
          <Title level={4} style={{ margin: 0 }}>
            {editingItem ? "Chỉnh sửa item" : "Thêm item mới"}
          </Title>
        }
        open={isModalVisible}
        onCancel={handleCloseModal}
        footer={null}
        width={700}
        style={{ top: 20 }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            type: "null",
            openBrowser: false,
            priority: 0,
          }}
        >
          <Form.Item label="Ảnh">
            <Upload
              accept="image/*"
              listType="picture-card"
              maxCount={1}
              showUploadList={false}
              beforeUpload={beforeUpload}
            >
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt="preview"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: 6,
                  }}
                />
              ) : (
                getUploadButton()
              )}
            </Upload>
            {imageUrl && (
              <div style={{ marginTop: 8 }}>
                <Button
                  size="small"
                  danger
                  onClick={() => {
                    setImage(null);
                    setImageUrl(null);
                  }}
                >
                  Xóa ảnh
                </Button>
              </div>
            )}
          </Form.Item>

          <Row gutter={16}>
            <Col span={16}>
              <Form.Item
                label="Loại"
                name="type"
                rules={[{ required: true, message: "Vui lòng chọn loại!" }]}
              >
                <Select size="large">
                  <Select.Option value="null">Không dùng</Select.Option>
                  <Select.Option value="url">Liên kết</Select.Option>
                  <Select.Option value="restaurant">Nhà hàng</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Độ ưu tiên"
                name="priority"
                tooltip="Số càng thấp thì hiển thị càng trước (mặc định: 0)"
              >
                <InputNumber
                  size="large"
                  min={0}
                  max={999}
                  style={{ width: "100%" }}
                  placeholder="0"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const type = getFieldValue("type");
              if (type === "null") return null;

              if (type === "url") {
                return (
                  <Row gutter={16}>
                    <Col span={16}>
                      <Form.Item
                        label="URL"
                        name="url"
                        rules={[
                          { required: true, message: "Vui lòng nhập URL!" },
                          { type: "url", message: "URL không hợp lệ!" },
                        ]}
                      >
                        <Input placeholder="https://..." size="large" />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label="Mở bằng trình duyệt"
                        name="openBrowser"
                        valuePropName="checked"
                      >
                        <Switch size="default" />
                      </Form.Item>
                    </Col>
                  </Row>
                );
              }

              if (type === "restaurant") {
                return (
                  <Form.Item
                    label="ID nhà hàng"
                    name="restaurant"
                    rules={[
                      { required: true, message: "Vui lòng nhập ID nhà hàng!" },
                    ]}
                  >
                    <Input placeholder="Nhập ID nhà hàng" size="large" />
                  </Form.Item>
                );
              }

              return null;
            }}
          </Form.Item>

          <Form.Item
            style={{ marginBottom: 0, textAlign: "right", marginTop: 24 }}
          >
            <Space size="middle">
              <Button onClick={handleCloseModal} size="large">
                Hủy
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={isProcessing}
                size="large"
                style={{
                  borderRadius: 6,
                  boxShadow: "0 2px 8px rgba(24, 144, 255, 0.2)",
                }}
              >
                {editingItem ? "Cập nhật" : "Tạo mới"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CustomModuleItemsList;
